/** 교사 질문 페이지 보기 옵션(결과 탭·정렬·토글) — 로컬에 기억해 페이지 재진입 시 복원 */

export type CommunicationSortOrder = 'number' | 'response' | 'random' | 'seat';
export type CommunicationResultViewMode = 'students' | 'accuracy' | 'breakdown';
export type CommunicationAccuracySortOrder = 'response' | 'alphabetical';
export type CommunicationBreakdownSortOrder =
  | 'correctness'
  | 'response'
  | 'alphabetical'
  | 'count';

const STORAGE_KEY = 'cp.presentationCommTeacherViewPrefs.v1';

export type PresentationCommunicationTeacherViewPrefs = {
  resultViewMode: CommunicationResultViewMode;
  sortOrder: CommunicationSortOrder;
  accuracySortOrder: CommunicationAccuracySortOrder;
  breakdownSortOrder: CommunicationBreakdownSortOrder;
  showResponse: boolean;
  showName: boolean;
  showAnswer: boolean;
  isSeatRotated: boolean;
  studentFontScale: number;
};

/** 처음 열 때 기본: 멤버별 응답 결과 + 번호순 */
export const DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS: PresentationCommunicationTeacherViewPrefs =
  {
    resultViewMode: 'students',
    sortOrder: 'number',
    accuracySortOrder: 'response',
    breakdownSortOrder: 'response',
    showResponse: true,
    showName: true,
    showAnswer: false,
    isSeatRotated: false,
    studentFontScale: 1,
  };

const RESULT_VIEW_MODES: CommunicationResultViewMode[] = ['students', 'accuracy', 'breakdown'];
const SORT_ORDERS: CommunicationSortOrder[] = ['number', 'response', 'random', 'seat'];
const ACCURACY_SORTS: CommunicationAccuracySortOrder[] = ['response', 'alphabetical'];
const BREAKDOWN_SORTS: CommunicationBreakdownSortOrder[] = [
  'correctness',
  'response',
  'alphabetical',
  'count',
];

function isOneOf<T extends string>(value: unknown, allowed: readonly T[]): value is T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value);
}

function clampFontScale(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS.studentFontScale;
  return Math.min(2.6, Math.max(0.8, Math.round(n * 10) / 10));
}

export function loadPresentationCommunicationTeacherViewPrefs(): PresentationCommunicationTeacherViewPrefs {
  if (typeof window === 'undefined') return { ...DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS };
    const parsed = JSON.parse(raw) as Partial<PresentationCommunicationTeacherViewPrefs>;
    if (!parsed || typeof parsed !== 'object') {
      return { ...DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS };
    }
    return {
      resultViewMode: isOneOf(parsed.resultViewMode, RESULT_VIEW_MODES)
        ? parsed.resultViewMode
        : DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS.resultViewMode,
      sortOrder: isOneOf(parsed.sortOrder, SORT_ORDERS)
        ? parsed.sortOrder
        : DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS.sortOrder,
      accuracySortOrder: isOneOf(parsed.accuracySortOrder, ACCURACY_SORTS)
        ? parsed.accuracySortOrder
        : DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS.accuracySortOrder,
      breakdownSortOrder: isOneOf(parsed.breakdownSortOrder, BREAKDOWN_SORTS)
        ? parsed.breakdownSortOrder
        : DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS.breakdownSortOrder,
      showResponse:
        typeof parsed.showResponse === 'boolean'
          ? parsed.showResponse
          : DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS.showResponse,
      showName:
        typeof parsed.showName === 'boolean'
          ? parsed.showName
          : DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS.showName,
      showAnswer:
        typeof parsed.showAnswer === 'boolean'
          ? parsed.showAnswer
          : DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS.showAnswer,
      isSeatRotated:
        typeof parsed.isSeatRotated === 'boolean'
          ? parsed.isSeatRotated
          : DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS.isSeatRotated,
      studentFontScale: clampFontScale(parsed.studentFontScale),
    };
  } catch {
    return { ...DEFAULT_PRESENTATION_COMM_TEACHER_VIEW_PREFS };
  }
}

export function savePresentationCommunicationTeacherViewPrefs(
  prefs: PresentationCommunicationTeacherViewPrefs
): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* quota / private mode */
  }
}
